import { AudioTrack, VoicingCheck } from "./types";
import { sameCueSpeaker } from "./cueSpeaker";

// Итог проверки дубля на слух меняется, а строка оригинала при этом стоит на месте: без этого
// сравнения ♪ строки показывал бы прежний итог.
const sameVoicingCheck = (a: VoicingCheck | undefined, b: VoicingCheck | undefined): boolean =>
    a === b ||
    (Boolean(a) &&
        Boolean(b) &&
        a!.ok === b!.ok &&
        (a!.heard ?? null) === (b!.heard ?? null) &&
        (a!.takes ?? null) === (b!.takes ?? null));

export function mergeTrackArrays(oldTracks: AudioTrack[], newTracks: AudioTrack[]): AudioTrack[] {
    const oldMap = new Map(oldTracks.map(t => [t.id, t]));
    return newTracks.map(newTrack => {
        const old = oldMap.get(newTrack.id);
        if (!old) return newTrack;
        if (old.start === newTrack.start && old.length === newTrack.length &&
            old.captions === newTrack.captions && old.file === newTrack.file &&
            // Новый дубль под тем же именем файла — новая версия, иначе звук играл бы из кэша.
            (old.file_v ?? null) === (newTrack.file_v ?? null) &&
            old.voice === newTrack.voice && old.speed === newTrack.speed &&
            old.translation === newTrack.translation &&
            // «Без автоправок» marks a tail the build will refuse; it changes without b moving.
            (old.manual_overlap_ms ?? null) === (newTrack.manual_overlap_ms ?? null) &&
            // ♪ and the broken-time mark read these; they change without the cue itself moving.
            (old.has_translation ?? null) === (newTrack.has_translation ?? null) &&
            (old.translation_timing_invalid ?? null) === (newTrack.translation_timing_invalid ?? null) &&
            (old.timing_invalid ?? null) === (newTrack.timing_invalid ?? null) &&
            // The mark names the server's words and offers «restore window» only for a broken window.
            (old.translation_timing_problem ?? null) === (newTrack.translation_timing_problem ?? null) &&
            (old.translation_window_broken ?? null) === (newTrack.translation_window_broken ?? null) &&
            (old.timing_problem ?? null) === (newTrack.timing_problem ?? null) &&
            // ♪ and 🔊 judge the pair's own times (the voicing server does the same).
            (old.translation_start ?? null) === (newTrack.translation_start ?? null) &&
            old.translation_slot_start === newTrack.translation_slot_start &&
            (old.translation_slot_end ?? null) === (newTrack.translation_slot_end ?? null) &&
            // ♪ warns while the voicing does not match its text (the server's ear check).
            sameVoicingCheck(old.translation_voicing_check, newTrack.translation_voicing_check) &&
            // Кусок фразы меняет номер, не сдвигаясь: удалили или склеили соседний кусок.
            (old.split_parent ?? null) === (newTrack.split_parent ?? null) &&
            (old.piece_index ?? null) === (newTrack.piece_index ?? null) &&
            (old.piece_count ?? null) === (newTrack.piece_count ?? null) &&
            (old.piece_repeats_text ?? null) === (newTrack.piece_repeats_text ?? null) &&
            (old.piece_shares_text ?? null) === (newTrack.piece_shares_text ?? null) &&
            // Перевод куска стал (или перестал быть) повтором соседнего: строка меняет надпись.
            (old.piece_dub_repeats ?? []).join(",") === (newTrack.piece_dub_repeats ?? []).join(",") &&
            (old.piece_dub_repeats_whole ?? null) === (newTrack.piece_dub_repeats_whole ?? null) &&
            // Реплику сняли с озвучки (⊘) или вернули (♪, «вернуть»), не сдвигая: строка перевода
            // говорит «снята с озвучки» или молчит, ⊘ спрашивает про дубль, если он есть.
            (old.translation_skip ?? null) === (newTrack.translation_skip ?? null) &&
            (old.translation_voiced ?? null) === (newTrack.translation_voiced ?? null) &&
            (old.translation_skipped_text ?? null) === (newTrack.translation_skipped_text ?? null) &&
            // Своя правка места ещё не в эфире: блок говорит «ждёт круга» на том же месте (P50m20g, E7).
            (old.timing_pending ?? null) === (newTrack.timing_pending ?? null) &&
            // Подпись «кто говорит»: голос перевода сменился, а строка стоит на месте.
            sameCueSpeaker(old.translation_speaker, newTrack.translation_speaker) &&
            (old.skip ?? null) === (newTrack.skip ?? null) &&
            // Реплику оригинала удалили (или вернули), а озвучка стоит на месте: блок становится
            // «озвучкой без реплики» и замолкает (4119e839, папка 1, 1:22, «Así es.», 02.10.2026).
            (old.orphan_voicing ?? null) === (newTrack.orphan_voicing ?? null)) {
            return old;
        }
        return newTrack;
    });
}
